import type { TFunction } from 'i18next';

import { ApiHttpError, isKnownAdsErrorClass, type AdsErrorClass, type AdsPlatform } from '@/types';

/**
 * 從 `ApiHttpError` 抽 error slug(`SCREAMING_SNAKE_CASE` 形狀字串)。
 *
 * **R42.12 對齊 BE spec(2026-05-18)**:BE Ads endpoints 把 slug 放 `error.message`
 * field(不是 `error.code`);其他 service(jobs / brand kits)仍可能放 `error.code`。
 * 兩個都讀:**優先 `error.message`**(新 spec)→ fallback `error.code`(舊 spec / 其他
 * service),避免 caller 端要 track BE convention。
 *
 * Slug 形狀守則:`^[A-Z][A-Z0-9_]{3,}$`(首字 A-Z、總長 ≥ 4),才當 slug;否則
 * (human-readable 文案 / 純 HTTP status code 數字 / 單字測試 placeholder 'X')視為沒拿到。
 * 長度限制是為了排除 `'X'` / `'OK'` 之類 placeholder,避免誤把 human readable 字段當 slug。
 */
const SLUG_PATTERN = /^[A-Z][A-Z0-9_]{3,}$/;

function isSlugShape(s: unknown): s is string {
  return typeof s === 'string' && SLUG_PATTERN.test(s);
}

export function getAdsErrorSlug(err: unknown): string | null {
  if (!(err instanceof ApiHttpError)) return null;
  const msg = err.error?.message;
  if (isSlugShape(msg)) return msg;
  const code = err.error?.code;
  if (isSlugShape(code)) return code;
  return null;
}

/**
 * 對齊 caller(`useTranslation('ads')`)的 t function 型別。
 *
 * Ads namespace 跟 workflow `JobsTranslate`(`features/jobs/errorMap.ts`)**獨立** —
 * docs/31 §6.1 明文警告 ads namespace 跟 workflow 不共用值集合 / i18n key,物理拆檔
 * 強制隔離(對齊 Software Architect panel)。
 */
export type AdsTranslate = TFunction<'ads'>;

/**
 * Stage 1 — Ads error → toast 文案中央化(對齊 R23 `features/jobs/errorMap.ts` pattern)。
 * -----------------------------------------------------------------------------
 * **R42.1 backend pivot** 對應錯誤碼整理:
 *
 * 新加(對應 `POST /oauth/link`):
 *   - 401 `AUTH_BROKEN` — cookie 有效但 user mapping 壞;caller 端應 redirect /login
 *   - 502 `LINK_UPSTREAM_FAILED` — perm 上游錯;FE toast retry
 *   - 500 `LINK_INTERNAL_FAILED` — Firestore 寫失敗;FE toast retry
 *
 * 砍掉(對應永遠不會回來的 OAuth callback / Discovery 狀態查詢):
 *   - `STATE_INVALID` / `OAUTH_DENIED` / `CUSTOMER_NOT_IN_DISCOVERY`
 *   - `parseOAuthCallbackError` / `oauthCallbackErrorMessage`(URL query 機制砍)
 *
 * 設計原則:
 *   - **純函式**:input → output;不彈 toast(caller 自己彈)
 *   - **不管 race**:caller 各自處理(eg.AUTH_BROKEN → router.replace('/login'))
 *   - **fallback 帶 debug 細節**:不認得的 code 走 generic 但回 `[status code msg]`
 *   - **i18n key 在 `ads` namespace**:caller 用 `useTranslation('ads')` 取 t
 *
 * 用法:
 * ```ts
 * } catch (e) {
 *   if (e instanceof ApiHttpError && e.error?.code === 'AUTH_BROKEN') {
 *     // 特殊路徑 — caller 自己 redirect,不彈 toast
 *     router.replace('/login');
 *     return;
 *   }
 *   showToast({ tone: 'error', message: mapAdsErrorMessage(e, t) });
 * }
 * ```
 */
export function mapAdsErrorMessage(err: unknown, t: AdsTranslate): string {
  if (!(err instanceof ApiHttpError)) {
    const msg = err instanceof Error ? err.message : String(err);
    return `${t('error.generic')} [${msg}]`;
  }

  // R42.12 — `getAdsErrorSlug` 讀 message/code 兩處,優先 message(對齊 2026-05-18 BE spec)
  const slug = getAdsErrorSlug(err) ?? '';
  const status = err.status;

  // 已知 ads error_class → i18n(R44.0 後含 NOT_CANCELLABLE / SYNC_JOB_NOT_FOUND
  // 已升 enum 第一公民,對齊 docs/35 Phase C handoff §5)
  if (isKnownAdsErrorClass(slug)) {
    return t(`error_class.${slug}` as const);
  }

  // Special non-class codes(對齊 docs/31 §3.3 + §6.1):
  if (slug === 'ASSET_NOT_FOUND') return t('error.assetNotFound');
  if (status === 401 || slug === 'AUTH_INVALID_TOKEN') return t('error.authLost');

  // Generic fallback with debug 細節
  const detail = `${status} ${slug} ${err.error?.message ?? err.message ?? ''}`.trim();
  return `${t('error.generic')} [${detail}]`;
}

/**
 * 判斷錯誤是否屬於 `AUTH_BROKEN`(caller 端應該走 router.replace('/login') 不彈 toast)。
 *
 * 對齊 R42.1 backend `POST /oauth/link` 401 response — cookie 有效但 user mapping 壞,
 * 走完整 re-login flow 才能修復(不是 refresh chain 能解決的)。
 *
 * 用法:
 * ```ts
 * } catch (e) {
 *   if (isAuthBrokenError(e)) {
 *     router.replace('/login');
 *     return;
 *   }
 *   showToast({ tone: 'error', message: mapAdsErrorMessage(e, t) });
 * }
 * ```
 */
export function isAuthBrokenError(err: unknown): boolean {
  if (!(err instanceof ApiHttpError)) return false;
  if (err.status === 401) return true;
  const slug = getAdsErrorSlug(err);
  return slug === 'AUTH_BROKEN';
}

/**
 * R289 — 409 `LINK_NO_USABLE_ACCOUNT` 的 `error.details`(FB-20260827-0001)。
 */
export interface LinkNoUsableAccountInfo {
  /**
   * 細粒度成因 —— BE 目前給 `no_ads_user`(帳號沒關聯任何 Ads 帳戶)/
   * `account_not_enabled`(帳戶未啟用)。
   *
   * ⚠️ **當開放字串讀,不要對它分支**(對齊 R275 `failure_code` 慣例):一份文案已同時
   * 涵蓋兩種可能(owner 2026-08-27 拍板),這個值只給觀測與客服查詢用。BE 日後加值
   * 不需要 FE 跟著改。
   */
  reason: string | null;
  /** 出問題那顆授權的 Google 帳號 email;BE 取不到時 wire 上**整個 key 不存在** → null。 */
  oauthEmail: string | null;
}

/**
 * R289 — 把 `POST /oauth/link` 的 **409 永久錯**認出來(FB-20260827-0001)。
 * -----------------------------------------------------------------------------
 * **為什麼要獨立一支判準而不是丟進 `mapAdsErrorMessage`**:這個錯**不是 toast 型**。
 * 授權本身成功了(Google 有發 token、我們也存下來了),失敗在下一步「向 Google 取帳戶
 * 清單」—— 而且**重試永遠不會成功**,必須使用者去 Google Ads 端處理。所以呈現要的是
 * 一張讀得完、不會自己消失的說明卡 + **收掉重試出口**,`mapAdsErrorMessage` 給不了。
 *
 * 事故本體:BE 原本把這個永久錯報成 502 `LINK_UPSTREAM_FAILED`,而那條的 FE 契約文案是
 * 「授權服務暫時無法連線,請稍後重試」→ 兩位使用者合計重試 **16 次**全部失敗,且**無法
 * 自行脫離**該狀態。⚠️ 所以 502 的文案**不可以**再套回這一條(負向鎖在 `errorMap.test`)。
 *
 * **判準用 slug 不用狀態碼**(BE docs/105 §1 明文):狀態碼只回答「這個錯能不能靠重試
 * 解決」,BE 日後若把 409 換成語意更貼的碼,只要 slug 不變 FE 就不會壞。
 *
 * ⚠️ **`details` 拿得到是有前提的**:BE 走 `HTTPException(detail=Error(...))`,經
 * `app/main.py` 的 handler 攤成 envelope `{success,error:{code,message,details}}`,FE
 * transport 的 `normalizeEnvelopeError` 原樣 spread 才留得住。若哪天 BE 改回 FastAPI 裸
 * `{detail:{…}}` 形狀,`extractDetailObjectError` 只留 code/message/reasonCode ——
 * **`oauth_email` 會被靜默吃掉、卡片降級成 fallback 文案而沒有任何錯誤徵兆**。
 *
 * @returns 命中回 details(缺欄位一律 null,不丟例外);不是這個錯回 `null`
 */
export function parseLinkNoUsableAccount(err: unknown): LinkNoUsableAccountInfo | null {
  if (!(err instanceof ApiHttpError)) return null;
  if (getAdsErrorSlug(err) !== 'LINK_NO_USABLE_ACCOUNT') return null;
  const d = err.error?.details;
  const raw = (typeof d === 'object' && d !== null ? d : {}) as {
    reason?: unknown;
    oauth_email?: unknown;
  };
  return {
    reason: typeof raw.reason === 'string' ? raw.reason : null,
    oauthEmail: typeof raw.oauth_email === 'string' ? raw.oauth_email : null,
  };
}

/**
 * 直接把 `error_class` enum(eg. sync_job.errorClass field)映射成 i18n 訊息。
 *
 * 用在「拿到的不是 `ApiHttpError`、而是已經 parse 好的 error_class 字串」的場景
 * (典型:`SyncJobDetail.errorClass` 從 Firestore listener 來,本身就是 enum,不是
 * HTTP 錯誤)。**不要**為了重用 `mapAdsErrorMessage` 而偽造 `ApiHttpError`-like 物件
 * 丟進去 —— 偽造物件不是 `ApiHttpError` instance,`mapAdsErrorMessage` 會 fall through
 * 到 `String(err)` 印出 `[object Object]`。
 *
 * 行為對齊 `mapAdsErrorMessage` 的 known-class 分支:
 *   - 已知 class → `t('error_class.<CLASS>')`
 *   - null / undefined / 未知 class → `t('error.reasonUnknown')`(跟 `getAdsErrorAction`
 *     的 unknown 分支同源,不誤導 user)
 */
export function mapAdsErrorClassMessage(
  errorClass: AdsErrorClass | string | null | undefined,
  t: AdsTranslate,
  platform?: AdsPlatform,
): string {
  // R319 — `ADS_QUOTA_EXCEEDED` **在兩個平台是不同的東西**,同一句話一定有一邊是錯的:
  //   Google = 日配額,明天才重置;Meta = app / 帳戶級**滑動窗**,約 30–60 分鐘就滑出。
  // 對 Meta 顯示 Google 那句(「將於明日重置」)會讓使用者白等一天,而且**零錯誤徵兆** ——
  // 訊息本身讀起來完全正常。⚠️ 平台不明時走中性句(不講重置時間):寧可少講,不要講錯。
  if (errorClass === 'ADS_QUOTA_EXCEEDED') {
    if (platform === 'meta') return t('error_class.ADS_QUOTA_EXCEEDED_META');
    if (platform === 'google') return t('error_class.ADS_QUOTA_EXCEEDED');
    return t('error_class.ADS_QUOTA_EXCEEDED_GENERIC');
  }
  if (typeof errorClass === 'string' && isKnownAdsErrorClass(errorClass)) {
    return t(`error_class.${errorClass}` as const);
  }
  return t('error.reasonUnknown');
}

/**
 * T7(docs/53 §A.4)— `POST /assets/imports` 逐筆 `error_class`(該筆 `status=FAILED`,
 * 整批仍 200)。FE 把 `error_class` 當**不透明字串**對到人話 i18n + 保底 fallback。
 *
 * 涵蓋 docs §A.4 全部 per-item class(圖文影 + 影片專屬 + 內部);未列舉的(BE 之後加
 * 新 class / GA SDK 映射的 `ADS_*`)走 `fallback`,不誤導 user。
 */
export const ASSET_IMPORT_ERROR_CLASSES: readonly string[] = [
  'LIBRARY_NOT_FOUND',
  'ASSET_CROSS_GROUP_FORBIDDEN',
  'LIBRARY_ITEM_NOT_FOUND',
  'ASSET_SOURCE_UNAVAILABLE',
  'EMPTY_TEXT_ASSET',
  'ADS_PARTIAL_FAILURE',
  'ADS_OAUTH_EXPIRED', // docs/53 §A.4:token 失效 / 撤銷 → 重新授權可解
  'ADS_ACTION_NOT_PERMITTED', // R303 docs/53 §A.4 2026-09-10:唯讀存取層級 → 重新授權**無效**,叫管理員升權
  'NEEDS_REAUTH',
  'UPLOAD_TIMEOUT',
  'YOUTUBE_FAILED',
  'YOUTUBE_REJECTED',
  'YOUTUBE_UNAVAILABLE',
  'INTERNAL_FAILED',
  // ── R323(BE docs/118 §B.2)— Meta 素材上傳的 per-item error_class ──────────
  // ⚠️ 不在這張表的碼會落到 `fallback`(通用失敗 + 原碼)。那是**刻意的保底**,
  // 但 BE 已經在文件裡列出來的碼就該有人話 —— 少一個,使用者就多看到一次
  // 「上傳失敗(META_MEDIA_INVALID)」這種讀了也不知道要做什麼的訊息。
  'META_TEXT_STANDALONE_UNSUPPORTED',
  'UNSUPPORTED_ASSET_KIND',
  'META_MEDIA_INVALID',
  'WORKFLOW_ASSET_NOT_FOUND',
  'NOT_PUBLISHABLE',
  'META_UPLOAD_NO_ID',
  'META_UPLOAD_UNVERIFIED',
  // Meta 端影片處理失敗的三個 `video_status` 值 —— 對使用者是同一件事(同一句文案),
  // 但碼分三個,所以三個都要列;合成一個會讓另外兩個落到 fallback。
  'META_VIDEO_ERROR',
  'META_VIDEO_UPLOAD_FAILED',
  'META_VIDEO_FAILED',
  // ⚠️ 這三個與 job 層同名但**語境不同**(這裡是 per-item 上傳失敗),文案各自寫。
  'ADS_PERMISSION_DENIED',
  'ADS_RESOURCE_MISSING',
  'ADS_QUOTA_EXCEEDED',
  'ADS_NETWORK_ERROR',
];

/**
 * R338 — **兩個平台都會回、但人話不一樣**的 per-item class(BE docs/118 §B.2)。
 *
 * 這兩個碼 Google 與 Meta 的上傳路徑都會送,而 Google 那份文案寫死了平台細節:
 *   - `ADS_OAUTH_EXPIRED` —「**Google Ads** 授權已失效」
 *   - `UPLOAD_TIMEOUT`   —「(**YouTube** 處理超過 30 分鐘)」
 *
 * ⚠️ 顯給 Meta 上傳失敗的人看,是把他指向一個根本沒發生的地方(他的影片在 Meta 轉檔,
 * 跟 YouTube 無關;要重授的是 Meta 那把)—— 而畫面上一切正常,只是答錯了平台。
 *
 * ⚠️ **只放「兩邊都會回」的碼**:`ADS_ACTION_NOT_PERMITTED`(Google 的存取層級)與
 * `META_*`(Meta 專屬)各自只會從一邊來,文案本來就該寫死那一邊,不要為了對稱而加。
 */
const IMPORT_ERROR_CLASS_META_VARIANTS: readonly string[] = ['ADS_OAUTH_EXPIRED', 'UPLOAD_TIMEOUT'];

/**
 * per-item `error_class` → 人話訊息(i18n key `import.errorClass.*`,ads namespace)。
 * 未知 class → `import.errorClass.fallback`(帶不透明 class 供 debug,不誤導)。
 *
 * `platform` 省略 = Google(既有 caller 零改);見
 * {@link IMPORT_ERROR_CLASS_META_VARIANTS} —— 只有那幾個碼有 `_META` 版本,其餘兩平台同句。
 *
 * `t` 採 loose 簽名(相容 wizard 的 `LooseT` 與 `TFunction`),避開動態 key 的
 * strict-augment 型別摩擦。
 */
export function mapAssetImportErrorClass(
  errorClass: string | null | undefined,
  t: (key: string, params?: Record<string, unknown>) => string,
  platform: AdsPlatform = 'google',
): string {
  if (typeof errorClass === 'string' && ASSET_IMPORT_ERROR_CLASSES.includes(errorClass)) {
    const suffix =
      platform === 'meta' && IMPORT_ERROR_CLASS_META_VARIANTS.includes(errorClass) ? '_META' : '';
    return t(`import.errorClass.${errorClass}${suffix}`);
  }
  return t('import.errorClass.fallback', { code: errorClass ?? '' });
}

/**
 * Backend `/oauth/start` / `/oauth/callback` 302 回 FE 時 URL `?error=` 帶的 slug —
 * 對外**鎖死**只這幾種(Google 三種 + Meta 多三種;docs/31 §3.5 / docs/109 §4)。
 *
 * slug 故意 vague,內部 trigger 細節(token 過期 / perm 5xx / upstream / Firestore 寫失敗
 * etc.)**完全不對 FE 曝光**。Unit test 鎖「不會 leak 內部關鍵字到對外白名單值」。
 *
 *   - `session_expired`    — session / 登入失效相關,引導重 login(兩平台共用)
 *   - `permission_denied`  — 帳號**沒 ads 權限**(perm 403);login 救不了,顯 toast 留原頁(Google)
 *   - `link_failed`        — 其他出錯,讓 user 重點 button(兩平台共用;Meta 的 flag 關著也走這條)
 *   - `scope_missing`      — R299 Meta:使用者在 FB 授權畫面**取消勾選 `ads_read`** → token
 *                            不落地(fail-closed)。文案講「需要允許讀取廣告成效」+ 給重試
 *                            (重走授權,Meta 會再問一次)
 *   - `app_not_live`       — R299 Meta:App 還在開發模式、這個 FB 帳號不在 App 角色內 →
 *                            Facebook 直接拒。**不給重試**(R289 紅線:重試不會成功的錯不留
 *                            再試入口)—— 要聯繫我們開通測試權限
 *   - `user_denied`        — R299 Meta:App 已 Live、使用者按了取消 → **靜默**回連結頁(非錯誤)
 *
 * ⚠️ `?provider=meta` 只是分文案(BE `build_error_redirect_url` 兩平台各自帶),slug 白名單
 * 是同一份 —— 不認得的 slug 一律 ignore,不分平台。
 */
export const OAUTH_ERROR_SLUGS = [
  'session_expired',
  'permission_denied',
  'link_failed',
  'scope_missing',
  'app_not_live',
  'user_denied',
] as const;

export type OAuthErrorSlug = (typeof OAUTH_ERROR_SLUGS)[number];

export function isKnownOAuthErrorSlug(s: string): s is OAuthErrorSlug {
  return (OAUTH_ERROR_SLUGS as readonly string[]).includes(s);
}

/**
 * Backend `/finalize` 階段 400/404 error code 對齊 FE 行為,分兩條路徑:
 *
 * **已連結過(redirect `/ads/accounts`)**:
 *   - 400 `DISCOVERY_NOT_DISCOVERED` — anchor 已 COMPLETED(stale sessionStorage 拿到舊
 *     discovery_id,但 anchor 那邊 finalize 過鎖死);user 已經 link 過了,不需要重 link,
 *     送他去 `/ads/accounts` 看已連的帳號列表
 *
 * **state 真壞(redirect `/ads/connect` 重 link)**:
 *   - 404 `DISCOVERY_NOT_FOUND` — discovery_id 在 BE 不存在(過期 / 拼錯)
 *   - 400 `CUSTOMER_NOT_IN_DISCOVERY` — 選的 customer 不在 discovered_accounts(state stale)
 *   - 400 `MANAGER_NOT_SELECTABLE` — 選了 MCC(FE 端 `checkModel="leaf"` 理論不該撞;
 *     撞到表 sessionStorage 跟 BE 認知 mismatch)
 *
 * 500/502 `INTERNAL_FAILED` 跟其他 5xx 走 toast retry,**不**砍 sessionStorage
 * (state 沒壞,只是上游錯)。
 *
 * **caller 端分流順序(R42.11)**:`isFinalizeAlreadyLinkedError` 先 check
 * (narrower 1 code)→ `isFinalizeStateInvalidError`(broader 4 codes 含同一條)
 * → toast retry。靠 short-circuit 達成分支:
 * ```ts
 * if (isFinalizeAlreadyLinkedError(err)) { redirect /ads/accounts; return; }
 * if (isFinalizeStateInvalidError(err)) { redirect /ads/connect; return; }
 * // 5xx — toast retry
 * ```
 */
const FINALIZE_ALREADY_LINKED_CODES: readonly string[] = ['DISCOVERY_NOT_DISCOVERED'];

const FINALIZE_STATE_INVALID_CODES: readonly string[] = [
  'DISCOVERY_NOT_FOUND',
  'DISCOVERY_NOT_DISCOVERED',
  'CUSTOMER_NOT_IN_DISCOVERY',
  'MANAGER_NOT_SELECTABLE',
];

export function isFinalizeAlreadyLinkedError(err: unknown): boolean {
  const slug = getAdsErrorSlug(err);
  return slug !== null && FINALIZE_ALREADY_LINKED_CODES.includes(slug);
}

export function isFinalizeStateInvalidError(err: unknown): boolean {
  const slug = getAdsErrorSlug(err);
  return slug !== null && FINALIZE_STATE_INVALID_CODES.includes(slug);
}

/**
 * R237(F90-8)— `GET /discoveries/{id}/accounts` 分頁 endpoint 的錯誤三分類。
 *
 * ⚠️ 跟 finalize 的分流**語意不同,不可共用**:finalize 的 `DISCOVERY_NOT_DISCOVERED`
 * = anchor 已 COMPLETED(user 已連結過 → 導 `/ads/accounts` 看清單);分頁 endpoint 的
 * `DISCOVERY_NOT_DISCOVERED` = DISCOVERING 進行中 / FAILED / TTL 殘片 —— 清單本身
 * 拿不到,唯一出口是**重走 link flow**(`/oauth/link` 冪等,重打會建新快照)。
 *
 * - `expired` — 404 `DISCOVERY_NOT_FOUND` / 400 `DISCOVERY_NOT_DISCOVERED`:
 *   toast「清單已過期」+ 導 `/ads/connect`(connect 頁 on-mount 會自動重打 link)
 * - `rateLimited` — 429(60/min;搜尋已 debounce,撞到多半是連打「載入更多」):
 *   顯提示留畫面,讓 user 稍候手動重試(不自動 retry 迴圈 —— 共用配額,雪崩風險)
 * - `generic` — 其他(500 / 網路):顯錯 + 重試鈕
 */
export type DiscoveryFetchErrorKind = 'expired' | 'rateLimited' | 'generic';

const DISCOVERY_LIST_EXPIRED_CODES: readonly string[] = [
  'DISCOVERY_NOT_FOUND',
  'DISCOVERY_NOT_DISCOVERED',
];

export function classifyDiscoveryFetchError(err: unknown): DiscoveryFetchErrorKind {
  const slug = getAdsErrorSlug(err);
  if (slug !== null && DISCOVERY_LIST_EXPIRED_CODES.includes(slug)) return 'expired';
  // 429 靠 status 兜底(R215:BE rate-limit 回應的 error.code 是數字,slug 撈不到)
  if (err instanceof ApiHttpError && err.status === 429) return 'rateLimited';
  return 'generic';
}

/**
 * `reasonKey` 的封閉 literal union — 用 literal(不用 `string`)讓 i18next strict-augmented
 * `t(action.reasonKey)` 能直接吃(對齊 HelpPage `titleKey` literal-union pattern;
 * 避免 HomeSidebar 那種 `t as LooseT` cast)。全部在 `ads` namespace `error.*` 底下。
 */
export type AdsFailureReasonKey =
  | 'error.reasonReauth'
  | 'error.reasonOpsAlert'
  | 'error.reasonLoginCustomerInvalid'
  | 'error.reasonQuota'
  /** R319 — Meta 的配額是滑動窗(約 30–60 分),與 Google 的日配額不同句;見 `getAdsErrorAction`。 */
  | 'error.reasonQuotaMeta'
  /** R319 — 平台不明時的中性句(不講重置時間:寧可少講,不要講錯)。 */
  | 'error.reasonQuotaGeneric'
  | 'error.reasonPartialFailure'
  | 'error.reasonNetwork'
  | 'error.reasonTokenTimeout'
  | 'error.reasonAccess'
  | 'error.reasonGone'
  | 'error.reasonReadOnly'
  | 'error.reasonUnknown';

/**
 * R44.2 — Sync worker-layer `error_class` 對 FE 動作的 discriminated union(對齊
 * docs/37 §3 FE action helper)。
 *
 * Sync_job 在 status=`FAILED` 時,`error_class` 有值;此 helper 把 worker-layer error
 * class(對齊 BE `app/ads/google/error_mapper.py`;R52 後 8 個 `ADS_*`,另含若干
 * HTTP-layer / generic class)分流成 4 種 FE 動作:
 *
 * - **`reauth`** — Google OAuth 端問題,引導 user 重新連結帳號(`/ads/connect`)
 * - **`retry`** — 暫時錯,可重試(用戶手動 / 自動);`autoRetry=true` 表 FE 端自動跑指數退避;
 *   `delayMs` 表建議延遲(對 quota 是 24h reset)
 * - **`opsAlert`** — BE / 系統錯,FE 無法自修;顯維運提示 + trace_id
 * - **`unknown`** — 預期外錯(`INTERNAL_FAILED` 不認 errorClass);顯 trace_id 讓 user 截圖
 *
 * **`reason` vs `reasonKey`**:
 * - `reasonKey` 是 `ads` namespace 的 i18n key(`error.reason*`)— **要顯給 user 的字串走它**
 *   (caller `t(action.reasonKey)`),不要把 `reason` raw 字串直接 render(避免硬編碼文案進 DOM /
 *   無法在地化)。
 * - `reason` 是 **診斷用 raw 英文字串**(log / 觀測 / unit test 斷言用,eg. 確認 MCC 設定錯有提到
 *   "MCC")— **不該直接 render**。保留它讓 console / 測試能讀到語意,但顯示一律走 `reasonKey`。
 *
 * **caller pattern**(對齊 docs/37 §3 範例 + AdsSyncPage 實作):
 * ```ts
 * const action = getAdsErrorAction(detail.errorClass);
 * switch (action.kind) {
 *   case 'reauth':  return <ReauthBanner cta={t('error.reauthCta')} />;
 *   case 'retry':   return <div>{t(action.reasonKey)}</div>;
 *   case 'opsAlert': return <OpsAlertBanner hint={t('error.opsAlertHint', { traceId })} />;
 *   case 'unknown':  return <UnknownErrorBanner traceId={detail.traceId} />;
 * }
 * ```
 */
export type AdsFailureAction =
  | { kind: 'reauth'; reason: string; reasonKey: AdsFailureReasonKey }
  | { kind: 'retry'; autoRetry: boolean; delayMs?: number; reason: string; reasonKey: AdsFailureReasonKey }
  | { kind: 'opsAlert'; reason: string; reasonKey: AdsFailureReasonKey }
  /**
   * R301 — 身分活著但**看不到 / 沒權限讀**這個帳戶(`ADS_CUSTOMER_NOT_ACCESSIBLE` /
   * `ADS_PERMISSION_DENIED`):**重新授權無效**,要去平台後台(Google Ads / Meta 企業管理平台)
   * 確認存取權;解除綁定是合理出口。⚠️ 與 `reauth` 分兩格是 docs/37 §3 2026-09-07 的原話,
   * 合成一格 = 叫使用者白跑一趟授權頁。
   */
  | { kind: 'access'; reason: string; reasonKey: AdsFailureReasonKey }
  /** R301 — 帳戶在平台端已不存在(`ADS_RESOURCE_MISSING`)→ 建議解除綁定。 */
  | { kind: 'gone'; reason: string; reasonKey: AdsFailureReasonKey }
  | { kind: 'unknown'; reason: string; reasonKey: AdsFailureReasonKey };

export function getAdsErrorAction(
  errorClass: AdsErrorClass | null | undefined,
  platform?: AdsPlatform,
): AdsFailureAction {
  switch (errorClass) {
    // `NEEDS_REAUTH`(R301)= job 層「此綁定所有身分都不能用」(docs/37 §3 2026-09-07;Meta docs/111 §4 同名)
    // `TOKEN_EXPIRED`(R306)= BE 白名單裡與 sync_status 同名的那顆(refresh token 死);重新授權可解。
    case 'ADS_OAUTH_EXPIRED':
    case 'OAUTH_EXPIRED':
    case 'AUTH_BROKEN':
    case 'NEEDS_REAUTH':
    case 'TOKEN_EXPIRED':
      return { kind: 'reauth', reason: 'OAuth revoked or expired', reasonKey: 'error.reasonReauth' };

    // R301 — 重新授權**無效**的兩格(身分活著,是帳戶端不給看)
    case 'ADS_CUSTOMER_NOT_ACCESSIBLE':
    case 'ADS_PERMISSION_DENIED':
      return {
        kind: 'access',
        reason: 'Identity alive but cannot access this account; check access in the platform console',
        reasonKey: 'error.reasonAccess',
      };
    case 'ADS_RESOURCE_MISSING':
      return { kind: 'gone', reason: 'Ad account deleted or hidden on the platform', reasonKey: 'error.reasonGone' };
    // R303 — 授權身分對此帳戶只有**唯讀**存取層級(Google `ACTION_NOT_PERMITTED`;只出現在匯入 / 上傳 per-item):
    // 重新授權無效(換 token 換不到更高的 Google Ads 權限),要管理員升權或換有編輯權限的帳號 → 同 `access` 一格
    case 'ADS_ACTION_NOT_PERMITTED':
      return {
        kind: 'access',
        reason: 'Authorized identity has read-only access to this ad account; ask an admin to raise the access level',
        reasonKey: 'error.reasonReadOnly',
      };

    case 'ADS_DEV_TOKEN_INVALID':
    case 'ADS_INVALID_QUERY':
    case 'CONFIG_ERROR':
    case 'INVALID_QUERY':
    case 'REPORTS_INTERNAL_FAILED':
      return { kind: 'opsAlert', reason: 'BE / system config error, ops handling', reasonKey: 'error.reasonOpsAlert' };

    case 'ADS_LOGIN_CUSTOMER_INVALID':
      // R52 docs/37 §3 — BE MCC / login-customer-id 設定或解析錯(NOT user OAuth 失效)。
      // kind 維持 opsAlert(走 AdsSyncPage opsAlert UI 分支);reason 專屬保留診斷語意(提及 MCC)。
      return {
        kind: 'opsAlert',
        reason: 'Sync config invalid (MCC login-customer-id), contact admin',
        reasonKey: 'error.reasonLoginCustomerInvalid',
      };

    // R319 — 同一個 class,兩個平台語意不同(見 `mapAdsErrorClassMessage` 的說明):
    // Google 是日配額(24h 重置),Meta 是滑動窗(約 30–60 分)。`delayMs` 也跟著分,
    // 因為它是「多久之後重試才有意義」的提示,抄錯邊會叫人白等一天。
    case 'ADS_QUOTA_EXCEEDED':
      if (platform === 'meta') {
        return {
          kind: 'retry',
          autoRetry: false,
          delayMs: 1_800_000,
          reason: 'Meta rate limit (sliding window), retry in ~30-60 min; re-auth does not help',
          reasonKey: 'error.reasonQuotaMeta',
        };
      }
      if (platform === 'google') {
        return {
          kind: 'retry',
          autoRetry: false,
          delayMs: 86_400_000,
          reason: 'Google Ads API quota exhausted, resets after 24h',
          reasonKey: 'error.reasonQuota',
        };
      }
      // 平台不明 → 不講重置時間(寧可少講,不要講錯);也不給 delayMs
      return {
        kind: 'retry',
        autoRetry: false,
        reason: 'Ad platform quota / rate limit; retry later. Re-auth does not help',
        reasonKey: 'error.reasonQuotaGeneric',
      };

    case 'ADS_PARTIAL_FAILURE':
      return {
        kind: 'retry',
        autoRetry: false,
        reason: 'Partial data missing, re-submit sync to retry',
        reasonKey: 'error.reasonPartialFailure',
      };

    // R306(BE docs/36 §0.A B12)— BE 解 perm token 撞 transient 重試到上界:**基礎設施問題,
    // 重新授權無效**,使用者無事可做(系統自動重試)。⚠️ 名字裡有 TOKEN 但**不是** reauth 那格。
    case 'TOKEN_TIMEOUT_EXHAUSTED':
      return {
        kind: 'retry',
        autoRetry: true,
        reason: 'Backend token resolution exhausted transient retries; system will retry',
        reasonKey: 'error.reasonTokenTimeout',
      };

    case 'ADS_NETWORK_ERROR':
    case 'PLATFORM_TRANSIENT':
      return {
        kind: 'retry',
        autoRetry: true,
        delayMs: 3000,
        reason: 'Transient network error, auto-retrying',
        reasonKey: 'error.reasonNetwork',
      };

    case 'SYNC_INTERNAL_FAILED':
    case 'INTERNAL_FAILED':
      // 已知的 internal-failed class — 顯式列出讓讀者知道它們刻意走 unknown(顯 trace_id)。
      return { kind: 'unknown', reason: 'Unknown error', reasonKey: 'error.reasonUnknown' };

    default:
      // null / undefined / 任何未列舉的 ads errorClass 統一走這裡(對齊 R42 既有「未認得
      // errorClass 走 generic fallback」防呆;BE 漂值寫進不認得的 class 時不誤導 user 重新授權)。
      // 合併原本獨立的 null / undefined case — 回傳跟 default 完全相同,落到 default 行為不變。
      return { kind: 'unknown', reason: 'Unknown error', reasonKey: 'error.reasonUnknown' };
  }
}
